import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from 'react-bootstrap';
import useHover from '../CustomHooks/useHover';

/*
 * Rail glyphs are drawn on a 28px box with a 24px glyph inside it, so the SVG box is
 * 28 to land the glyph at the 24px the frame draws — the same footprint the icomoon
 * `font-size: var(--28px)` rule gave the font icons this rail used before.
 */
const MENU_ICON_SIZE = 28;

function MenuItem({
  pathName,
  linkTo,
  iconClass,
  IconComponent,
  titleKey,
  activeClassList,
  trafficLightStyle,
  aTag,
  badgeData,
  activeId,
  isMobileMenu,
  stateClass = '',
  activeExcludeList = [],
}) {
  const { t } = useTranslation('common');
  const { t: sMaster } = useTranslation('systemMaster');
  const { hover, coords, handleMouseEnter, handleMouseLeave } = useHover();

  const menuTitleStyle = (coordsParam) => ({
    position: 'fixed',
    top: `${coordsParam.top}px`,
    left: 44,
    zIndex: 999,
    opacity: 1,
  });

  /**
   * Active matching is a substring test, so a longer path that merely contains a shorter entry
   * lights that entry up too — `/product-costing-v2` matches `product-costing`. `activeExcludeList`
   * lets a caller name the paths that must NOT count as a match for this item, so two menu entries
   * whose paths share a prefix can each highlight on their own route.
   */
  const isExcluded = activeExcludeList.some((link) => pathName.includes(link));
  const isActive = !isExcluded && (activeId ? pathName.includes(activeId) : activeClassList.some((link) => pathName.includes(link)));
  const menuTitle = t(titleKey, { defaultValue: sMaster(titleKey) });
  /**
   * Draws the rail glyph. An SVG component wins over the icomoon font class, so a menu
   * whose icon only exists in the bombiix-icon set can supply it directly.
   * @returns {JSX.Element} The icon element.
   */
  const renderIcon = () =>
    IconComponent ? <IconComponent size={MENU_ICON_SIZE} className={trafficLightStyle || ''} /> : <i className={`${iconClass} ${trafficLightStyle || ''}`} />;

  const renderLinkContent = (isTitleRequired) => (
    <>
      {renderIcon()}
      {isTitleRequired && menuTitle}
      {badgeData?.count > 0 && (
        <Badge bg="secondary" className={badgeData.colour || 'amber'}>
          {badgeData?.count}
        </Badge>
      )}
    </>
  );

  let linkElement;

  if (aTag && isMobileMenu) {
    linkElement = (
      <a aria-label="a tag" className="btn-subnav" href={linkTo}>
        {renderLinkContent(true)}
      </a>
    );
  } else if (aTag) {
    linkElement = (
      <a onMouseEnter={handleMouseEnter} aria-label="a tag" onMouseLeave={handleMouseLeave} className="btn-subnav" href={linkTo}>
        {renderIcon()}
      </a>
    );
  } else if (isMobileMenu) {
    linkElement = <Link to={linkTo}>{renderLinkContent(true)}</Link>;
  } else {
    linkElement = (
      <Link onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} to={linkTo}>
        {renderLinkContent()}
      </Link>
    );
  }

  return (
    <li className={`${isActive ? 'active' : ''} ${stateClass}`.trim()}>
      {linkElement}
      {hover && (
        <span className={`${trafficLightStyle} hover-title`} style={menuTitleStyle(coords)}>
          {menuTitle}
        </span>
      )}
    </li>
  );
}

MenuItem.defaultProps = { IconComponent: null };

export default MenuItem;
